import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Modal, Form, InputGroup, Alert, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const SolicitudPrestamo = () => {
  const { user } = useAuth();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [plantilla, setPlantilla] = useState(null);
  const [camposFormulario, setCamposFormulario] = useState([]);
  const [nuevaSolicitud, setNuevaSolicitud] = useState({});
  const [solicitudEditando, setSolicitudEditando] = useState(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [prestamosCompletados, setPrestamosCompletados] = useState({});

  const extraerCamposDeVariables = useCallback(() => {
    if (!plantilla) return;

    console.log('Plantilla cargada:', plantilla);
    console.log('Usuario completo:', user);
    console.log('Cédula del usuario:', user?.cedula);

    const plantillaStr = JSON.stringify(plantilla);
    const regex = /\{\{([a-zA-Z_]+)\}\}/g;
    const variablesEncontradas = new Set();
    let match;
    
    while ((match = regex.exec(plantillaStr)) !== null) {
      variablesEncontradas.add(match[1].toLowerCase());
    }

    console.log('Variables encontradas:', Array.from(variablesEncontradas));

    // Configuración de campos predefinidos
    const camposConfig = {
      'nombre_completo': { 
        tipo: 'text', 
        etiqueta: 'Nombre Completo', 
        requerido: false, 
        valorInicial: user?.name || '',
        readOnly: true 
      },
      'cedula': { 
        tipo: 'text', 
        etiqueta: 'Cédula', 
        requerido: false, 
        valorInicial: user?.cedula || '',
        readOnly: true 
      },
      'numero_socio': { 
        tipo: 'text', 
        etiqueta: 'Número de Socio', 
        requerido: false, 
        valorInicial: user?.socioInfo?.numeroSocio || '',
        readOnly: true 
      },
      'telefono': { 
        tipo: 'tel', 
        etiqueta: 'Teléfono', 
        requerido: false, 
        valorInicial: user?.phone || '',
        readOnly: false 
      },
      'email': { 
        tipo: 'email', 
        etiqueta: 'Email', 
        requerido: false, 
        valorInicial: user?.email || '',
        readOnly: true 
      },
      'direccion': { 
        tipo: 'text', 
        etiqueta: 'Dirección', 
        requerido: false, 
        valorInicial: user?.address || '',
        readOnly: false 
      },
      'ciudad': { 
        tipo: 'text', 
        etiqueta: 'Ciudad', 
        requerido: false, 
        valorInicial: '',
        readOnly: false 
      },
      'monto': { 
        tipo: 'number', 
        etiqueta: 'Monto Solicitado', 
        requerido: true, 
        valorInicial: '', 
        prefix: '$',
        min: config?.prestamos?.montoMinimo || 100,
        max: config?.prestamos?.montoMaximo || 50000,
        step: '0.01'
      },
      'plazo': { 
        tipo: 'number', 
        etiqueta: 'Plazo (meses)', 
        requerido: true, 
        valorInicial: '',
        min: config?.prestamos?.plazoMinimo || 1,
        max: config?.prestamos?.plazoMaximo || 60
      },
      'tasa_interes': { 
        tipo: 'number', 
        etiqueta: 'Tasa de Interés (%)', 
        requerido: false, 
        valorInicial: config?.prestamos?.tasaInteresMensual || '',
        readOnly: true 
      }
    };

    const campos = [];
    const valoresIniciales = {};

    // Agregar campos encontrados en la plantilla
    variablesEncontradas.forEach(variable => {
      // Excluir variables que no deben ser campos editables
      if (variable === 'fecha_actual' || 
          variable === 'nombresolicuitante' || 
          variable === 'cedulsolicuitante') {
        // Agregar a valoresIniciales pero NO mostrar como campo
        if (camposConfig[variable]) {
          valoresIniciales[variable] = camposConfig[variable].valorInicial;
        } else if (variable === 'nombresolicuitante') {
          valoresIniciales[variable] = user?.name || '';
        } else if (variable === 'cedulsolicuitante') {
          valoresIniciales[variable] = user?.cedula || '';
        }
        return; // No agregar como campo editable
      }
      
      if (camposConfig[variable]) {
        const campo = { ...camposConfig[variable], nombre: variable };
        campos.push(campo);
        valoresIniciales[variable] = campo.valorInicial;
      } else {
        // Si la variable no está predefinida, crear un campo genérico
        const etiqueta = variable
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        const campo = {
          tipo: 'text',
          etiqueta: etiqueta,
          requerido: false,
          valorInicial: '',
          readOnly: false,
          nombre: variable
        };
        campos.push(campo);
        valoresIniciales[variable] = '';
      }
    });

    // Ordenar campos: requeridos primero
    campos.sort((a, b) => {
      if (a.requerido && !b.requerido) return -1;
      if (!a.requerido && b.requerido) return 1;
      return 0;
    });

    console.log('Campos del formulario:', campos);

    setCamposFormulario(campos);
    setNuevaSolicitud(valoresIniciales);
  }, [plantilla, config, user]);

  useEffect(() => {
    if (plantilla && config && user) {
      extraerCamposDeVariables();
    }
  }, [plantilla, config, user, extraerCamposDeVariables]);

  // Verificar qué préstamos están pagados
  useEffect(() => {
    const verificarPrestamos = async () => {
      for (const solicitud of solicitudes) {
        if (solicitud.estado === 'aprobada') {
          await verificarPrestamoPagado(solicitud._id);
        }
      }
    };
    
    if (solicitudes.length > 0) {
      verificarPrestamos();
    }
  }, [solicitudes]);

  useEffect(() => {
    fetchSolicitudes();
    fetchConfiguracion();
    fetchPlantilla();
  }, []);

  const fetchPlantilla = async () => {
    try {
      const response = await axios.get('/api/plantilla-prestamo');
      if (response.data) {
        setPlantilla(response.data);
      }
    } catch (error) {
      console.error('Error al cargar plantilla:', error);
    }
  };

  const fetchConfiguracion = async () => {
    try {
      setLoadingConfig(true);
      const response = await axios.get('/api/configuracion');
      setConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const fetchSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/solicitudes-prestamo/mis-solicitudes');
      setSolicitudes(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast.error('Error al cargar las solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = async () => {
    if (config && user?.socioInfo?.fechaIngreso) {
      const mesesRequeridos = config.prestamos?.mesesEsperaParaPrestamo ?? 0;
      
      if (mesesRequeridos > 0) {
        const fechaIngreso = new Date(user.socioInfo.fechaIngreso);
        const mesesDesdeIngreso = Math.floor((new Date() - fechaIngreso) / (1000 * 60 * 60 * 24 * 30));
        
        if (mesesDesdeIngreso < mesesRequeridos) {
          toast.warning(`Debes esperar ${mesesRequeridos} meses desde tu ingreso. Te faltan ${mesesRequeridos - mesesDesdeIngreso} meses.`);
          return;
        }
      }
    }
    
    // Recargar plantilla y configuración antes de abrir el modal
    await fetchPlantilla();
    await fetchConfiguracion();
    
    // Limpiar campos editando y reinicializar con valores por defecto
    setSolicitudEditando(null);
    
    // Reinicializar el formulario con valores por defecto después de un pequeño delay
    // para permitir que los datos se actualicen
    setTimeout(() => {
      if (plantilla && config && user) {
        extraerCamposDeVariables();
      }
    }, 100);
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setShowPreview(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNuevaSolicitud(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVistaPrevia = (e) => {
    e.preventDefault();
    
    const camposRequeridos = camposFormulario.filter(c => c.requerido);
    const faltantes = camposRequeridos.filter(c => !nuevaSolicitud[c.nombre]);
    
    if (faltantes.length > 0) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    if (nuevaSolicitud.monto && config) {
      const monto = parseFloat(nuevaSolicitud.monto);
      if (monto < config.prestamos.montoMinimo || monto > config.prestamos.montoMaximo) {
        toast.error(`El monto debe estar entre $${config.prestamos.montoMinimo} y $${config.prestamos.montoMaximo}`);
        return;
      }
    }

    if (nuevaSolicitud.plazo && config) {
      const plazo = parseInt(nuevaSolicitud.plazo);
      if (plazo < config.prestamos.plazoMinimo || plazo > config.prestamos.plazoMaximo) {
        toast.error(`El plazo debe estar entre ${config.prestamos.plazoMinimo} y ${config.prestamos.plazoMaximo} meses`);
        return;
      }
    }

    setShowPreview(true);
  };

  const handleSubmit = async () => {
    try {
      const monto = parseFloat(nuevaSolicitud.monto);
      const plazo = parseInt(nuevaSolicitud.plazo);
      const tasaMensual = (config.prestamos?.tasaInteresMensual || 0) / 100;
      
      // Calcular valores
      const interesTotal = monto * tasaMensual * plazo;
      const totalAPagar = monto + interesTotal;
      const cuotaMensual = totalAPagar / plazo;
      
      // Preparar datos del formulario (convertir a objeto plano)
      const datosFormulario = {};
      Object.keys(nuevaSolicitud).forEach(key => {
        datosFormulario[key] = nuevaSolicitud[key];
      });
      
      // Datos de la solicitud
      const solicitudData = {
        monto,
        plazo,
        tasaInteres: config.prestamos?.tasaInteresMensual || 0,
        cuotaMensual,
        interesTotal,
        totalAPagar,
        datosFormulario,
        plantillaUsada: plantilla
      };
      
      // Si está editando, actualizar; si no, crear
      if (solicitudEditando) {
        await axios.patch(`/api/solicitudes-prestamo/${solicitudEditando._id}`, solicitudData);
        toast.success('Solicitud de préstamo actualizada exitosamente');
      } else {
        await axios.post('/api/solicitudes-prestamo', solicitudData);
        toast.success('Solicitud de préstamo creada exitosamente');
      }
      
      setShowPreview(false);
      handleCloseModal();
      
      // Limpiar formulario
      setNuevaSolicitud({});
      setSolicitudEditando(null);
      
      // Recargar solicitudes
      fetchSolicitudes();
    } catch (error) {
      console.error('Error al guardar solicitud:', error);
      toast.error(error.response?.data?.message || 'Error al guardar la solicitud de préstamo');
    }
  };

  const renderizarPlantilla = () => {
    if (!plantilla) {
      return (
        <div>
          <h3 style={{ textAlign: 'center', marginBottom: '30px' }}>SOLICITUD DE PRÉSTAMO</h3>
          <p style={{ textAlign: 'right' }}>
            Fecha de Solicitud: {new Date().toLocaleDateString('es-ES')}<br/>
            Ciudad: ............................
          </p>
          <p style={{ marginTop: '30px' }}>
            Señores<br/>
            UNION FAMILIAS
          </p>
          <p>
            Apellidos y Nombres: {user?.name}<br/>
            Cédula: {user?.cedula}
          </p>
          <p style={{ marginTop: '20px' }}>
            <strong>PRESENTE.-</strong>
          </p>
          <p style={{ textAlign: 'justify', marginTop: '20px' }}>
            Por medio de la presente hago llegar ante usted una solicitud para que me sea otorgada un préstamo personal.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Solicito el préstamo por la cantidad de ${nuevaSolicitud.monto} comprometiéndome a pagar la totalidad del mismo en un periodo de {nuevaSolicitud.plazo} meses. Con su respectivo interés.
          </p>
          <p style={{ textAlign: 'justify' }}>
            Esperando poder contar con su ayuda y comprensión.
          </p>
          <div style={{ marginTop: '80px', textAlign: 'center' }}>
            {user.socioInfo?.firma ? (
              <>
                <img src={user.socioInfo.firma} alt="Firma" style={{ maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto 2px' }} />
                <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 10px' }}></div>
              </>
            ) : (
              <div style={{ borderTop: '1px solid #000', width: '200px', margin: '0 auto 10px' }}></div>
            )}
            <p style={{ marginTop: '0px', fontSize: '12px' }}>Firma</p>
          </div>
        </div>
      );
    }

    // Renderizar plantilla estructurada
    const estilos = plantilla.estilos || {};
    const margenSuperior = estilos.margenSuperior || '3';
    const margenes = estilos.margenes || '2';
    const fuenteTitulo = estilos.fuenteTitulo || '16';
    const fuenteTexto = estilos.fuenteTexto || '12';
    const interlineado = estilos.interlineado || '1.5';
    const alineacionGeneral = estilos.alineacionGeneral || 'justify';

    // Función para reemplazar variables dinámicamente
    const reemplazarVariable = (variable) => {
      const varName = variable.replace(/\{\{|\}\}/g, '').toLowerCase();
      return nuevaSolicitud[varName] || user?.name || user?.cedula || '';
    };

    return (
      <div style={{ 
        padding: `${margenSuperior}cm ${margenes}cm`,
        fontSize: `${fuenteTexto}px`,
        lineHeight: interlineado,
        textAlign: alineacionGeneral
      }}>
        {/* Título */}
        <h3 style={{ 
          textAlign: 'center', 
          marginBottom: '30px',
          fontSize: `${fuenteTitulo}px`,
          fontWeight: 'bold'
        }}>
          {plantilla.titulo || 'SOLICITUD DE PRÉSTAMO'}
        </h3>

        {/* Encabezado */}
        {plantilla.encabezado && (
          <div style={{ 
            textAlign: plantilla.encabezado.alineacion || 'right',
            marginBottom: '20px'
          }}>
            {plantilla.encabezado.mostrarFecha && (
              <div>
                {plantilla.encabezado.textoFecha || 'Fecha de Solicitud:'} {new Date().toLocaleDateString('es-ES')}
              </div>
            )}
            {plantilla.encabezado.mostrarLugar && (
              <div>
                {(() => {
                  let textoLugar = plantilla.encabezado.textoLugar || 'Ciudad:';
                  // Reemplazar variables en el texto del lugar
                  const regex = /\{\{([a-zA-Z_]+)\}\}/g;
                  textoLugar = textoLugar.replace(regex, (match, varName) => {
                    const varLower = varName.toLowerCase();
                    if (nuevaSolicitud[varLower]) return nuevaSolicitud[varLower];
                    if (varLower === 'ciudad') return nuevaSolicitud.ciudad || '';
                    return '';
                  });
                  return textoLugar || (nuevaSolicitud.ciudad ? nuevaSolicitud.ciudad : '............................');
                })()}
              </div>
            )}
          </div>
        )}

        {/* Sección de Datos */}
        {plantilla.seccionDatos && (
          <div style={{ marginTop: '30px', marginBottom: '20px' }}>
            {plantilla.seccionDatos.titulo && (
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {plantilla.seccionDatos.titulo.replace(/\\n/g, '\n')}
              </div>
            )}
            {plantilla.seccionDatos.campos && plantilla.seccionDatos.campos.map((campo, index) => {
              if (!campo.visible) return null;
              
              let valor = campo.variable;
              
              // Si contiene variables {{variable}}, reemplazarlas
              if (valor && valor.includes('{{')) {
                const regex = /\{\{([a-zA-Z_]+)\}\}/g;
                valor = valor.replace(regex, (match, varName) => {
                  const varLower = varName.toLowerCase();
                  if (nuevaSolicitud[varLower]) return nuevaSolicitud[varLower];
                  if (varLower === 'nombre_completo') return user?.name || '';
                  if (varLower === 'cedula') return user?.cedula || '';
                  if (varLower === 'numero_socio') return user?.socioInfo?.numeroSocio || '';
                  if (varLower === 'email') return user?.email || '';
                  if (varLower === 'telefono') return user?.phone || '';
                  if (varLower === 'area') return user?.socioInfo?.area || nuevaSolicitud.area || '';
                  if (varLower === 'cargo') return user?.socioInfo?.cargo || nuevaSolicitud.cargo || '';
                  return match;
                });
              } else {
                // Si no tiene variables, usar datos del usuario según la etiqueta
                const etiqueta = campo.etiqueta.toLowerCase();
                if (etiqueta.includes('nombre')) {
                  valor = user?.name || valor;
                } else if (etiqueta.includes('cédula') || etiqueta.includes('cedula')) {
                  valor = user?.cedula || valor;
                } else if (etiqueta.includes('área') || etiqueta.includes('area')) {
                  valor = user?.socioInfo?.area || nuevaSolicitud.area || valor;
                } else if (etiqueta.includes('cargo')) {
                  valor = user?.socioInfo?.cargo || nuevaSolicitud.cargo || valor;
                }
              }
              
              return (
                <div key={index} style={{ marginBottom: '5px' }}>
                  <strong>{campo.etiqueta}</strong> {valor}
                </div>
              );
            })}
          </div>
        )}

        {/* Saludo */}
        {plantilla.cuerpo?.saludo && (
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <strong>{plantilla.cuerpo.saludo}</strong>
          </div>
        )}

        {/* Párrafos del cuerpo */}
        {plantilla.cuerpo?.parrafos && plantilla.cuerpo.parrafos.map((parrafo, index) => {
          if (!parrafo.visible) return null;
          
          let texto = parrafo.texto;
          const regex = /\{\{([a-zA-Z_]+)\}\}/g;
          const partes = [];
          let lastIndex = 0;
          let match;
          
          // Dividir el texto en partes, identificando las variables
          while ((match = regex.exec(texto)) !== null) {
            // Agregar texto antes de la variable
            if (match.index > lastIndex) {
              partes.push(texto.substring(lastIndex, match.index));
            }
            
            // Agregar la variable reemplazada en negrita
            const varName = match[1].toLowerCase();
            let valor = nuevaSolicitud[varName] || match[0];
            
            // Casos especiales
            if (varName === 'fecha_actual') {
              valor = new Date().toLocaleDateString('es-ES', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
            
            partes.push(<strong key={`var-${index}-${match.index}`}>{valor}</strong>);
            
            lastIndex = regex.lastIndex;
          }
          
          // Agregar el texto restante
          if (lastIndex < texto.length) {
            partes.push(texto.substring(lastIndex));
          }
          
          return (
            <p key={index} style={{ 
              textAlign: alineacionGeneral,
              marginBottom: '15px'
            }}>
              {partes}
            </p>
          );
        })}

        {/* Despedida y Firma */}
        {plantilla.despedida && (
          <div style={{ 
            marginTop: '80px',
            textAlign: plantilla.despedida.alineacion || 'center'
          }}>
            {user.socioInfo?.firma ? (
              <>
                <img src={user.socioInfo.firma} alt="Firma" style={{ maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto 15px' }} />
                <div style={{ 
                  borderTop: '1px solid #000', 
                  width: '200px', 
                  margin: plantilla.despedida.alineacion === 'center' ? '0 auto 10px' : '0 0 10px',
                  marginBottom: '10px'
                }}></div>
              </>
            ) : (
              plantilla.despedida.mostrarLineasFirma && (
                <div style={{ 
                  borderTop: '1px solid #000', 
                  width: '200px', 
                  margin: plantilla.despedida.alineacion === 'center' ? '0 auto 10px' : '0 0 10px',
                  marginBottom: '10px'
                }}></div>
              )
            )}
            <div style={{ fontSize: '12px' }}>{plantilla.despedida.texto || 'Firma'}</div>
          </div>
        )}
      </div>
    );
  };

  const getEstadoBadge = (estado, isPagado = false) => {
    // Si está pagado, mostrar "PAGO COMPLETO" en verde
    if (isPagado) {
      return (
        <Badge bg="success">
          <i className="fas fa-check-circle me-1"></i>
          PAGO COMPLETO
        </Badge>
      );
    }

    const badges = {
      pendiente: { variant: 'warning', text: 'Pendiente', icon: 'clock' },
      aprobada: { variant: 'success', text: 'Aprobada', icon: 'check-circle' },
      rechazada: { variant: 'danger', text: 'Rechazada', icon: 'times-circle' }
    };
    const badge = badges[estado] || badges.pendiente;
    return (
      <Badge bg={badge.variant}>
        <i className={`fas fa-${badge.icon} me-1`}></i>
        {badge.text}
      </Badge>
    );
  };

  // Función para verificar si un préstamo está completamente pagado
  const verificarPrestamoPagado = useCallback(async (solicitudId) => {
    // Si ya está en caché, devolverlo
    if (prestamosCompletados.hasOwnProperty(solicitudId)) {
      return prestamosCompletados[solicitudId];
    }

    try {
      // Hacer petición al backend para obtener cronograma
      const response = await axios.get(`/api/app/prestamos/${solicitudId}/amortizacion`);
      const cronograma = response.data?.data || [];
      
      if (cronograma.length > 0) {
        const ultimoPeriodo = cronograma[cronograma.length - 1];
        const pagado = parseFloat(ultimoPeriodo.saldoRestante) === 0 || parseFloat(ultimoPeriodo.saldoTotalConInteres) === 0;
        
        // Cachear resultado
        setPrestamosCompletados(prev => ({
          ...prev,
          [solicitudId]: pagado
        }));
        
        return pagado;
      }
    } catch (error) {
      // Si falla, devolver false
      return false;
    }
    
    return false;
  }, [prestamosCompletados]);

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMoneda = (monto) => {
    if (isNaN(monto)) return '$0.00';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(monto);
  };

  const handleViewPreview = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setNuevaSolicitud(solicitud.datosFormulario || {});
    setShowPreviewModal(true);
  };

  const handleEditSolicitud = (solicitud) => {
    setSolicitudEditando(solicitud);
    setNuevaSolicitud(solicitud.datosFormulario || {});
    setShowModal(true);
  };

  const handleDeleteSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta solicitud? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axios.delete(`/api/solicitudes-prestamo/${solicitudId}`);
      toast.success('Solicitud eliminada exitosamente');
      fetchSolicitudes();
    } catch (error) {
      console.error('Error al eliminar solicitud:', error);
      toast.error(error.response?.data?.message || 'Error al eliminar la solicitud');
    }
  };

  if (loading || loadingConfig) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-3">Cargando solicitudes...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-file-invoice-dollar me-2"></i>
                      Mis Solicitudes de Préstamo
                    </h2>
                    <p className="text-muted mb-0">
                      Gestiona tus solicitudes de préstamo
                    </p>
                  </div>
                  <Button variant="primary" onClick={handleShowModal} size="lg">
                    <i className="fas fa-plus me-2"></i>
                    Nueva Solicitud
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {user?.socioInfo && (
          <Row className="mb-4">
            <Col md={3}>
              <Card className="card-custom">
                <Card.Body className="text-center">
                  <i className="fas fa-user-circle fa-3x text-primary mb-2"></i>
                  <h6 className="mb-0">Número de Socio</h6>
                  <h4 className="text-primary">{user.socioInfo.numeroSocio || 'N/A'}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-custom">
                <Card.Body className="text-center">
                  <i className="fas fa-calendar-alt fa-3x text-success mb-2"></i>
                  <h6 className="mb-0">Fecha de Ingreso</h6>
                  <h5 className="text-success">
                    {user.socioInfo.fechaIngreso ? formatFecha(user.socioInfo.fechaIngreso) : 'N/A'}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-custom">
                <Card.Body className="text-center">
                  <i className="fas fa-piggy-bank fa-3x text-info mb-2"></i>
                  <h6 className="mb-0">Ahorros</h6>
                  <h4 className="text-info">{formatMoneda(user.socioInfo.ahorros || 0)}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="card-custom">
                <Card.Body className="text-center">
                  <i className="fas fa-hand-holding-usd fa-3x text-warning mb-2"></i>
                  <h6 className="mb-0">Préstamos Activos</h6>
                  <h4 className="text-warning">{user.socioInfo.prestamosActivos || 0}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Historial de Solicitudes
                </h5>
              </Card.Header>
              <Card.Body>
                {solicitudes.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No tienes solicitudes registradas. Haz clic en "Nueva Solicitud" para comenzar.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>N° Solicitud</th>
                          <th>Fecha</th>
                          <th>Monto</th>
                          <th>Plazo</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {solicitudes.map((solicitud) => {
                          const isPagado = prestamosCompletados[solicitud._id] || false;
                          return (
                            <tr key={solicitud._id}>
                              <td><strong>{solicitud.numeroSolicitud}</strong></td>
                              <td>{formatFecha(solicitud.fechaSolicitud)}</td>
                              <td><strong className="text-primary">{formatMoneda(solicitud.monto)}</strong></td>
                              <td>{solicitud.plazo} meses</td>
                              <td>{getEstadoBadge(solicitud.estado, isPagado)}</td>
                              <td>
                                {/* Desktop: botones individuales */}
                                <div className="d-none d-md-flex gap-2">
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    title="Ver detalles"
                                    onClick={() => handleViewPreview(solicitud)}
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                  <Button 
                                    variant="outline-warning" 
                                    size="sm" 
                                    title="Ver detalles del crédito"
                                    onClick={() => {
                                      setSelectedSolicitud(solicitud);
                                      setShowDetallesModal(true);
                                    }}
                                  >
                                    <i className="fas fa-info-circle"></i>
                                  </Button>
                                  {solicitud.estado === 'pendiente' && (
                                    <>
                                      <Button 
                                        variant="outline-info" 
                                        size="sm" 
                                        title="Editar solicitud"
                                        onClick={() => handleEditSolicitud(solicitud)}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        title="Eliminar solicitud"
                                        onClick={() => handleDeleteSolicitud(solicitud._id)}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </Button>
                                    </>
                                  )}
                                </div>

                                {/* Mobile: dropdown menu */}
                                <div className="d-md-none">
                                  <Dropdown>
                                    <Dropdown.Toggle 
                                      variant="outline-primary" 
                                      size="sm"
                                      id={`dropdown-${solicitud._id}`}
                                    >
                                      <i className="fas fa-ellipsis-v"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      <Dropdown.Item onClick={() => handleViewPreview(solicitud)}>
                                        <i className="fas fa-eye me-2"></i>Ver detalles
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => {
                                        setSelectedSolicitud(solicitud);
                                        setShowDetallesModal(true);
                                      }}>
                                        <i className="fas fa-info-circle me-2"></i>Detalles del crédito
                                      </Dropdown.Item>
                                      {solicitud.estado === 'pendiente' && (
                                        <>
                                          <Dropdown.Divider />
                                          <Dropdown.Item onClick={() => handleEditSolicitud(solicitud)}>
                                            <i className="fas fa-edit me-2"></i>Editar
                                          </Dropdown.Item>
                                          <Dropdown.Item onClick={() => handleDeleteSolicitud(solicitud._id)} className="text-danger">
                                            <i className="fas fa-trash me-2"></i>Eliminar
                                          </Dropdown.Item>
                                        </>
                                      )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal show={showModal && !showPreview} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-invoice-dollar me-2"></i>
            Nueva Solicitud de Préstamo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleVistaPrevia}>
            {camposFormulario.length > 0 ? (
              <Row>
                {camposFormulario.map((campo, index) => (
                  <Col md={campo.tipo === 'textarea' ? 12 : 6} key={index}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {campo.etiqueta} {campo.requerido && '*'}
                      </Form.Label>
                      {campo.prefix ? (
                        <InputGroup>
                          <InputGroup.Text>{campo.prefix}</InputGroup.Text>
                          <Form.Control
                            type={campo.tipo}
                            name={campo.nombre}
                            value={nuevaSolicitud[campo.nombre] || ''}
                            onChange={handleInputChange}
                            required={campo.requerido}
                            readOnly={campo.readOnly}
                            min={campo.min}
                            max={campo.max}
                            step={campo.step}
                          />
                        </InputGroup>
                      ) : (
                        <Form.Control
                          type={campo.tipo}
                          name={campo.nombre}
                          value={nuevaSolicitud[campo.nombre] || ''}
                          onChange={handleInputChange}
                          required={campo.requerido}
                          readOnly={campo.readOnly}
                          min={campo.min}
                          max={campo.max}
                          step={campo.step}
                        />
                      )}
                      {campo.min && campo.max && (
                        <Form.Text className="text-muted">
                          Rango permitido: {campo.min} - {campo.max}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert variant="warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                No se ha configurado una plantilla de préstamo. Contacta al administrador.
              </Alert>
            )}

            {/* Información del Crédito */}
            {nuevaSolicitud.monto && nuevaSolicitud.plazo && config && (
              <Card className="mb-3 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-calculator me-2"></i>
                    Información del Crédito
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-1">
                        <small className="text-muted">Monto Solicitado</small>
                        <h5 className="text-primary mb-0">
                          {formatMoneda(parseFloat(nuevaSolicitud.monto))}
                        </h5>
                      </div>
                      <div className="mb-1">
                        <small className="text-muted">Plazo</small>
                        <h5 className="text-info mb-0">
                          {nuevaSolicitud.plazo} meses
                        </h5>
                      </div>
                      <div className="mb-1">
                        <small className="text-muted">Tasa de Interés Mensual</small>
                        <h5 className="text-warning mb-0">
                          {config.prestamos?.tasaInteresMensual || 0}%
                        </h5>
                      </div>
                      {nuevaSolicitud.monto && nuevaSolicitud.plazo && (() => {
                        const monto = parseFloat(nuevaSolicitud.monto);
                        const tasaMensual = (config.prestamos?.tasaInteresMensual || 0) / 100;
                        const interesMensual = monto * tasaMensual;
                        return (
                          <div className="mb-1">
                            <small className="text-muted">Interés Mensual</small>
                            <h5 className="text-danger mb-0">
                              {formatMoneda(interesMensual)}
                            </h5>
                          </div>
                        );
                      })()}
                    </Col>
                    <Col md={6}>
                      {(() => {
                        const monto = parseFloat(nuevaSolicitud.monto);
                        const plazo = parseInt(nuevaSolicitud.plazo);
                        const tasaMensual = (config.prestamos?.tasaInteresMensual || 0) / 100;
                        
                        // Calcular usando INTERÉS SIMPLE
                        let cuotaMensual = 0;
                        let interesTotal = 0;
                        let totalAPagar = 0;
                        
                        if (tasaMensual > 0) {
                          // Interés Simple: Interés = Capital × Tasa × Tiempo
                          interesTotal = monto * tasaMensual * plazo;
                          totalAPagar = monto + interesTotal;
                          cuotaMensual = totalAPagar / plazo;
                        } else {
                          cuotaMensual = monto / plazo;
                          totalAPagar = monto;
                          interesTotal = 0;
                        }
                        
                        return (
                          <>
                            <div className="mb-1">
                              <small className="text-muted">Cuota Mensual</small>
                              <h4 className="text-success mb-0">
                                {formatMoneda(cuotaMensual)}
                              </h4>
                            </div>
                            <div className="mb-1">
                              <small className="text-muted">Interés Total</small>
                              <h5 className="text-danger mb-0">
                                {formatMoneda(interesTotal)}
                              </h5>
                            </div>
                            <div className="mb-1">
                              <small className="text-muted">Total a Pagar</small>
                              <h4 className="text-dark mb-0">
                                {formatMoneda(totalAPagar)}
                              </h4>
                            </div>
                          </>
                        );
                      })()}
                    </Col>
                  </Row>
                  <Alert variant="info" className="mb-0 mt-2">
                    <div className="mb-2">
                      <strong><i className="fas fa-file-invoice me-1"></i> Resumen del Crédito:</strong>
                    </div>
                    <small>
                      {(() => {
                        const monto = parseFloat(nuevaSolicitud.monto);
                        const plazo = parseInt(nuevaSolicitud.plazo);
                        const tasaMensual = (config.prestamos?.tasaInteresMensual || 0) / 100;
                        const interesTotal = monto * tasaMensual * plazo;
                        const totalAPagar = monto + interesTotal;
                        const cuotaMensual = totalAPagar / plazo;
                        
                        return (
                          <>
                            Usted está solicitando un préstamo de <strong>{formatMoneda(monto)}</strong> a pagar en <strong>{plazo} meses</strong> con una tasa de interés del <strong>{config.prestamos?.tasaInteresMensual}%</strong> mensual. 
                            Esto significa que pagará una cuota fija de <strong>{formatMoneda(cuotaMensual)}</strong> cada mes durante {plazo} meses. 
                            Al finalizar el plazo, habrá pagado un total de <strong>{formatMoneda(totalAPagar)}</strong>, 
                            de los cuales <strong>{formatMoneda(interesTotal)}</strong> corresponden a intereses.
                          </>
                        );
                      })()}
                    </small>
                  </Alert>
                </Card.Body>
              </Card>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button variant="info" type="submit" disabled={camposFormulario.length === 0}>
                <i className="fas fa-eye me-2"></i>
                Vista Previa
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            Vista Previa de Solicitud de Préstamo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '40px', 
            minHeight: '600px',
            fontFamily: 'Arial, sans-serif'
          }}>
            {renderizarPlantilla()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            <i className="fas fa-arrow-left me-2"></i>
            Volver a Editar
          </Button>
          <Button variant="success" onClick={handleSubmit}>
            <i className="fas fa-paper-plane me-2"></i>
            Enviar Solicitud
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" fullscreen="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            Vista Previa de Solicitud de Préstamo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#f5f5f5', padding: '20px' }}>
          {selectedSolicitud && (
            <div style={{ 
              backgroundColor: 'white', 
              padding: '40px', 
              minHeight: '600px',
              fontFamily: 'Arial, sans-serif'
            }}>
              {renderizarPlantilla()}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Detalles del Crédito */}
      <Modal show={showDetallesModal} onHide={() => setShowDetallesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calculator me-2"></i>
            Detalles del Crédito
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSolicitud && (
            <div>
              <Card className="mb-3 border-primary">
                <Card.Header className="bg-primary text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-file-invoice me-2"></i>
                    Información de la Solicitud
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Número de Solicitud</small>
                        <h5 className="mb-0">{selectedSolicitud.numeroSolicitud}</h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Fecha de Solicitud</small>
                        <h5 className="mb-0">{formatFecha(selectedSolicitud.fechaSolicitud)}</h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Estado</small>
                        <div className="mt-1">{getEstadoBadge(selectedSolicitud.estado)}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-money-bill-wave me-2"></i>
                    Detalles del Crédito
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Monto Solicitado</small>
                        <h4 className="text-primary mb-0">
                          {formatMoneda(selectedSolicitud.monto)}
                        </h4>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Plazo</small>
                        <h5 className="text-info mb-0">
                          {selectedSolicitud.plazo} meses
                        </h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Tasa de Interés Mensual</small>
                        <h5 className="text-warning mb-0">
                          {selectedSolicitud.tasaInteres}%
                        </h5>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <small className="text-muted">Cuota Mensual</small>
                        <h4 className="text-success mb-0">
                          {formatMoneda(selectedSolicitud.cuotaMensual)}
                        </h4>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Interés Total</small>
                        <h5 className="text-danger mb-0">
                          {formatMoneda(selectedSolicitud.interesTotal)}
                        </h5>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Total a Pagar</small>
                        <h4 className="text-dark mb-0">
                          {formatMoneda(selectedSolicitud.totalAPagar)}
                        </h4>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Alert variant="info">
                <div className="mb-2">
                  <strong><i className="fas fa-file-invoice me-1"></i> Resumen del Crédito:</strong>
                </div>
                <small>
                  Usted está solicitando un préstamo de <strong>{formatMoneda(selectedSolicitud.monto)}</strong> a pagar en <strong>{selectedSolicitud.plazo} meses</strong> con una tasa de interés del <strong>{selectedSolicitud.tasaInteres}%</strong> mensual. 
                  Esto significa que pagará una cuota fija de <strong>{formatMoneda(selectedSolicitud.cuotaMensual)}</strong> cada mes durante {selectedSolicitud.plazo} meses. 
                  Al finalizar el plazo, habrá pagado un total de <strong>{formatMoneda(selectedSolicitud.totalAPagar)}</strong>, 
                  de los cuales <strong>{formatMoneda(selectedSolicitud.interesTotal)}</strong> corresponden a intereses.
                </small>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetallesModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SolicitudPrestamo;
